import React, { FC, useMemo, useState } from 'react';
import classNames from 'classnames';

import CTA from 'Src/components/buttons/CTA';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { Image } from 'Src/feature/image/Image';
import { useImageLoader } from 'Src/feature/image/useImageLoader';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { useLocation } from 'Src/feature/provision/useLocation';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useScreenOrientationChange } from 'Src/feature/use-screen/useScreenOrientationChange';
import useRoute from 'Src/routes/useRoute';

import styles from './FlipAndTipLocationLogo.module.scss';

const FlipAndTipLocationLogo: FC = () => {
  const log = useLogger().getSubLogger({ name: 'FlipAndTipLocationLogo' });
  const { route, navigate, crumbs } = useRoute();
  const invalidateCheck = useCheck.query.invalidate();
  const terminal = useTerminal.query.get();

  const branding = useLocation.query.getBranding();
  const paymentFlow = usePaymentFlow();

  const [isCustomerFacing, isCustomerFacingSet] = useState(false);

  useScreenOrientationChange({
    onChange: ({ angle }) => {
      const _isCustomerFacing = angle >= 90;
      isCustomerFacingSet(_isCustomerFacing);
    },
  });

  const onClick = () => {
    paymentFlow.afterFlipAndTip();
    const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : -1;
    // invalidate the check so we fetch it when we arrive back on the payment screen
    invalidateCheck.getOne(checkId);
    navigate(crumbs.pos.check.payment.main.interpolate({
      checkId: route.params.active.checkId,
      itemSelect: route.params.value.one,
    }));
  };

  const brandingURL = useMemo(() => {
    const locationId = terminal.data?.location.id;
    if (!locationId) return undefined;
    if (!branding.data?.urls || !branding.data.urls.length) return undefined;

    // sort the urls based off of the search param "v" that holds a unix timestamp
    return branding.data.urls
      .sort((a, b) => {
        try {
          const url = {
            a: new URL(a),
            b: new URL(b),
          };
          const param = {
            a: url.a.searchParams.get('v') ?? '0',
            b: url.b.searchParams.get('v') ?? '0',
          };
          return parseInt(param.b, 10) -parseInt(param.a, 10);
        } catch (error) {
          log.error('brandingURL.sort', error);
          return -1;
        }
      }).find((url) => url.includes(`location/${locationId}`));
  }, [branding.data?.urls, log, terminal.data?.location.id]);

  const imageLoader = useImageLoader({ src: brandingURL });

  return (
    <CTA
      className={classNames(styles.root, {
        [styles.is_customer_facing]: isCustomerFacing,
        [styles.has_image]: imageLoader.isValid,
      })}
      onClick={onClick}
    >
      <Image loader={imageLoader} />
    </CTA>
  );
};

export { FlipAndTipLocationLogo };
